/**
 * The Mintly email chrome, wrapped around a template body for preview.
 *
 * Every colour here is a LITERAL, deliberately. This markup is rendered inside
 * an iframe and, in production, by Gmail and Outlook — neither of which has the
 * Control Center's CSS custom properties, and email clients do not support
 * custom properties at all. It previously used var(--ink-850) and friends,
 * which resolved to nothing and made the preview a poor likeness of the email
 * it is meant to be previewing.
 *
 * This is the one place in frontend/src where hex literals are correct.
 */

const MAIL_BG = '#f4f5f7';
const CARD_BG = '#ffffff';
const RULE = '#e5e7eb';
const TEXT = '#171717';
const TEXT_MUTED = '#6b7280';
const TEXT_DIM = '#9ca3af';
const LINK = '#0369a1';

/** Highlight for a token with no sample value, so gaps are visible. */
export const UNFILLED_MARK = (name: string) =>
  `<span style="background:#fef3c7;color:#92400e;font-weight:600;padding:0 3px;border-radius:3px;">{{${name}}}</span>`;

export type PreviewDevice = 'desktop' | 'mobile';

/** Rendered width per device. Email tables cap at 600px on desktop. */
export const DEVICE_WIDTH: Record<PreviewDevice, number> = {
  desktop: 600,
  mobile: 375,
};

export function wrapInLayout(
  previewText: string,
  content: string,
  showUnsubscribeLinks = true,
  device: PreviewDevice = 'desktop',
): string {
  const width = DEVICE_WIDTH[device];

  const unsubscribeFooter = showUnsubscribeLinks
    ? `
            <table cellpadding="0" cellspacing="0" border="0" style="margin:12px 0 0 0;">
              <tr>
                <td style="border:1px solid ${RULE};border-radius:4px;">
                  <a href="https://www.trymintly.com/unsubscribe"
                     style="display:inline-block;padding:6px 14px;font-size:12px;color:${TEXT_MUTED};text-decoration:none;font-family:'Lato',Helvetica,Arial,sans-serif;">
                    Unsubscribe
                  </a>
                </td>
                <td style="padding-left:12px;">
                  <a href="https://www.trymintly.com/jobseeker/dashboard"
                     style="font-size:11px;color:${TEXT_DIM};text-decoration:underline;font-family:'Lato',Helvetica,Arial,sans-serif;">
                    Manage preferences
                  </a>
                </td>
              </tr>
            </table>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1.0"/>
  <title>Preview</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Jost:wght@400;600;700&family=Lato:wght@400;700&display=swap');
    body { margin:0;padding:0;background:${MAIL_BG};font-family:'Lato',Helvetica,Arial,sans-serif; }
  </style>
</head>
<body>
  <div style="display:none;max-height:0;overflow:hidden;">${previewText}</div>
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${MAIL_BG};">
    <tr><td align="center" style="padding:${device === 'mobile' ? '16px 8px' : '32px 16px'};">
      <table width="${width}" cellpadding="0" cellspacing="0" border="0"
             style="max-width:${width}px;width:100%;background:${CARD_BG};border-radius:4px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <tr>
          <td style="background:${CARD_BG};padding:16px ${device === 'mobile' ? '20px' : '40px'};border-bottom:1px solid ${RULE};">
            <table width="100%" cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="vertical-align:middle;">
                  <img src="https://www.trymintly.com/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fmintly-logo-transparent.70aaca53.png&amp;w=1920&amp;q=75"
                       height="36" alt="Mintly" border="0"
                       style="display:inline-block;vertical-align:middle;height:36px;width:auto;"/>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:${device === 'mobile' ? '24px 20px 20px' : '36px 40px 28px 40px'};color:${TEXT};">
            ${content}
          </td>
        </tr>
        <tr><td style="padding:0 ${device === 'mobile' ? '20px' : '40px'};"><hr style="border:none;border-top:1px solid ${RULE};margin:0;"/></td></tr>
        <tr>
          <td style="padding:20px ${device === 'mobile' ? '20px 24px' : '40px 28px 40px'};background:${MAIL_BG};">
            <p style="margin:0 0 6px 0;font-size:13px;color:${TEXT_MUTED};font-family:'Lato',Helvetica,Arial,sans-serif;">
              Warm regards,<br/><strong style="color:${TEXT};">The Mintly Team</strong>
            </p>
            <p style="margin:10px 0 0 0;font-size:11px;color:${TEXT_DIM};line-height:1.6;font-family:'Lato',Helvetica,Arial,sans-serif;">
              You're receiving this email because you signed up on
              <a href="https://www.trymintly.com" style="color:${LINK};text-decoration:none;">trymintly.com</a>.
            </p>
            ${unsubscribeFooter}
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
